import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { EnumCommodity } from '../../../api'
import {
  AddProcedureDocument,
  NewProcedure,
  NewProcedureParameter,
  ProcedureParameter
} from '../../../api/Argentia/endpoints/procedures'
import {
  AnnexedParameters,
  DocumentTypes,
  ProcedureStates,
  ProcedureTypes
} from '../../../api/Argentia/enums'
import { SubjectModel } from '../../../pages/clients/clients.model'
import { ProcedureModel } from '../../../pages/comunicationCenter/tabs/procedures/comunicationProcedures.model'
import { AnnexedParamFormMode } from './proceduresTable.model'

type ProcedureHandler = (
  formData: any,
  item: ProcedureModel,
  procedureType: 'procedure' | 'procedureDocument'
) => {
  type: ProcedureTypes | DocumentTypes
  parameters: NewProcedureParameter[] | ProcedureParameter[]
  originProcedureId?: number
  state?: number
}

const notImplementedHandler: ProcedureHandler = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn('Handler no implementado para esta combinación')
  }
  return {
    type: ProcedureTypes.CambioSINCambios, // valor por defecto sin uso
    parameters: []
  }
}

const handlers: Record<AnnexedParamFormMode, Record<EnumCommodity, ProcedureHandler>> = {
  [AnnexedParamFormMode.NULL]: {
    // valor por defecto sin uso exigido por typescript
    [EnumCommodity.GasNatural]: notImplementedHandler,
    [EnumCommodity.Electricidad]: notImplementedHandler
  },
  [AnnexedParamFormMode.REINSTATEMENT]: {
    [EnumCommodity.Electricidad]: (formData, item, procedureType) => {
      const requiredParameters = [
        {
          type: AnnexedParameters.TIPO_REPOSICION,
          value: formData.reinstatementType ?? ''
        }
      ]
      return {
        type: ProcedureTypes.Reposicion,
        originProcedureId: formData.originProcedureId,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    },
    [EnumCommodity.GasNatural]: (formData, item, procedureType) => {
      const requiredParameters = [
        {
          type: AnnexedParameters.TIPO_REPOSICION,
          value: formData.reinstatementType ?? ''
        }
      ]
      return {
        type: ProcedureTypes.Reposicion_GAS,
        originProcedureId: formData.originProcedureId,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    }
  },
  [AnnexedParamFormMode.SEND_REINSTATEMENT_REPLY]: {
    [EnumCommodity.Electricidad]: (formData, item, procedureType) => ({
      type: DocumentTypes.Paso_15,
      state: formData.rejectionReason ? ProcedureStates.Rechazado : ProcedureStates.Aceptado,
      parameters: [...getCommonOptionalParameters(formData, item.id, procedureType)]
    }),
    [EnumCommodity.GasNatural]: (formData, item, procedureType) => ({
      type: DocumentTypes.A29,
      state: formData.rejectionReason ? ProcedureStates.Rechazado : ProcedureStates.Aceptado,
      parameters: [
        ...(formData.comments
          ? [
              {
                type: AnnexedParameters.COMENTARIOS,
                value: formData.comments
              }
            ]
          : []),
        ...(formData.rejectionReason
          ? [
              {
                type: AnnexedParameters.GAS_MOTIVO_RECHAZO_REPOSICION,
                value: formData.rejectionReason
              }
            ]
          : [])
      ]
    })
  },
  [AnnexedParamFormMode.WITHDRAWAL]: {
    [EnumCommodity.GasNatural]: (formData, item, procedureType) => {
      const requiredParameters = [
        {
          type: AnnexedParameters.TIPO_DESISTIMIENTO,
          value: formData.withdrawalType ?? ''
        }
      ]
      return {
        type: ProcedureTypes.Desistimiento_GAS,
        originProcedureId: formData.originProcedureId,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    },
    [EnumCommodity.Electricidad]: (formData, item, procedureType) => {
      const requiredParameters = [
        {
          type: AnnexedParameters.TIPO_DESISTIMIENTO,
          value: formData.withdrawalType ?? ''
        }
      ]
      return {
        type: ProcedureTypes.Desistimiento_PWR,
        originProcedureId: formData.originProcedureId,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    }
  },
  [AnnexedParamFormMode.INCIDENT_REPLY]: {
    [EnumCommodity.GasNatural]: (formData, item, procedureType) => {
      const requiredParameters = [
        {
          type: AnnexedParameters.GAS_CONTESTACION_INCIDENCIA,
          value: formData.incidentReplyType ?? ''
        }
      ]

      return {
        type: DocumentTypes.A26,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    },
    [EnumCommodity.Electricidad]: (formData, item, procedureType) => {
      const requiredParameters = [
        {
          type: AnnexedParameters.PWR_CONTESTACION_INCIDENCIA,
          value: formData.incidentReplyType ?? ''
        }
      ]
      return {
        type:
          item.type === ProcedureTypes.Baja_Suministro_PWR
            ? DocumentTypes.Paso_16
            : DocumentTypes.Paso_13,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    }
  },
  [AnnexedParamFormMode.SEND_CLAIM_INFORMATION]: {
    [EnumCommodity.GasNatural]: (formData, item, procedureType) => {
      const requiredParameters =
        formData.informationType != undefined
          ? [
              {
                type: AnnexedParameters.GAS_TIPO_INFORMACION_ADICIONAL,
                value: formData.informationType ?? ''
              }
            ]
          : []
      return {
        type: DocumentTypes.A26,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    },
    [EnumCommodity.Electricidad]: (formData, item, procedureType) => {
      const requiredParameters =
        formData.informationType != undefined
          ? [
              {
                type: AnnexedParameters.PWR_TIPO_INFORMACION_ADICIONAL,
                value: formData.informationType ?? ''
              }
            ]
          : []
      return {
        type: DocumentTypes.Paso_04,
        parameters: [
          ...requiredParameters,
          ...getCommonOptionalParameters(formData, item.id, procedureType)
        ]
      }
    }
  },
  [AnnexedParamFormMode.CANCELLATION]: {
    [EnumCommodity.GasNatural]: (formData, item, procedureType) => ({
      type: ProcedureTypes.Anulacion_Contratacion_GAS,
      parameters: [
        {
          type: AnnexedParameters.SOLICITUD_ANULADA,
          value: item?.code ?? ''
        },
        ...getCommonOptionalParameters(formData, item.id, procedureType)
      ],
      originProcedureId: item?.id
    }),
    [EnumCommodity.Electricidad]: (formData, item, procedureType) => ({
      type: mapDocumentTypeForCancellation(item?.type!),
      parameters: [],
      state: ProcedureStates.Anulacion_solicitada
    })
  }
}

export const mapNewProcedure = (
  annexedParamFormMode: AnnexedParamFormMode,
  formData: any,
  commodity: EnumCommodity,
  item: ProcedureModel
): NewProcedure => {
  const procedureType: 'procedure' | 'procedureDocument' = 'procedure'
  const handler = handlers[annexedParamFormMode]?.[commodity]

  if (!handler) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`No handler found for mode: ${annexedParamFormMode} and commodity: ${commodity}`)
    }
  }

  const { type, parameters, ...otherParams } = handler(formData, item, procedureType) as {
    type: ProcedureTypes
    parameters: NewProcedureParameter[]
  }

  return {
    code: '',
    cups: item?.cups,
    clientData: { id: item?.clientData?.id } as SubjectModel,
    type,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters,
    ...otherParams
  }
}

export const mapNewProcedureDocument = (
  annexedParamFormMode: AnnexedParamFormMode,
  formData: any,
  commodity: EnumCommodity,
  item: ProcedureModel
): AddProcedureDocument => {
  const procedureType: 'procedure' | 'procedureDocument' = 'procedureDocument'
  const handler = handlers[annexedParamFormMode]?.[commodity]

  if (!handler) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`No handler found for mode: ${annexedParamFormMode} and commodity: ${commodity}`)
    }
  }

  const { type, parameters, state } = handler(formData, item, procedureType) as {
    type: DocumentTypes
    parameters: ProcedureParameter[]
    state: ProcedureStates
  }

  return {
    //id: item!.id,
    idProcedure: item!.id,
    type,
    date: moment().toDate(),
    state: state ?? ProcedureStates.Aceptado,
    url: '.',
    parameters
  }
}

const mapDocumentTypeForCancellation = (procedureType: ProcedureTypes): DocumentTypes => {
  let documentType: DocumentTypes | undefined

  switch (procedureType) {
    case ProcedureTypes.CambioSINCambios_PWR:
    case ProcedureTypes.CambioCONCambios_PWR_Adm:
    case ProcedureTypes.CambioCONCambios_PWR_Tec:
    case ProcedureTypes.CambioCONCambios_PWR_Adm_y_Tec:
    case ProcedureTypes.Reclamacion_PWR_Tramitacion:
    case ProcedureTypes.Reclamacion_PWR_Factura:
    case ProcedureTypes.Reclamacion_PWR_CUPS:
    case ProcedureTypes.Reposicion:
      documentType = DocumentTypes.Paso_08
      break
    case ProcedureTypes.Alta_Nuevo_Suministro_PWR:
    case ProcedureTypes.Modificacion_PWR_Tec:
    case ProcedureTypes.Modificacion_PWR_Adm:
    case ProcedureTypes.Modificacion_PWR_Adm_y_Tec:
    case ProcedureTypes.Modificacion_Titular_PWR:
    case ProcedureTypes.Modificacion_Titular_y_Tec_PWR:
      documentType = DocumentTypes.Paso_06
      break
    case ProcedureTypes.Baja_Suministro_PWR:
    case ProcedureTypes.CorteSuministroPWR:
      documentType = DocumentTypes.Paso_03
      break
    default:
      documentType = DocumentTypes.Paso_08
      break
  }

  return documentType
}

const getCommonOptionalParameters = (
  formData: any,
  itemId: number,
  procedureType: 'procedure' | 'procedureDocument'
): ProcedureParameter[] | NewProcedureParameter[] => {
  const optionalParams: NewProcedureParameter[] = []

  if (formData.comments)
    optionalParams.push({
      type: AnnexedParameters.COMENTARIOS,
      value: formData.comments
    })
  if (formData.description)
    optionalParams.push({
      type: AnnexedParameters.DESCRIPCION_INFORMACION_ADICIONAL,
      value: formData.description
    })
  if (formData.rejectionReason)
    optionalParams.push({
      type: AnnexedParameters.MOTIVO_RECHAZO_REPOSICION,
      value: formData.rejectionReason
    })
  if (formData.newContact)
    optionalParams.push({
      type: AnnexedParameters.NUEVA_PERSONA_CONTACTO,
      value: formData.newContact
    })
  if (formData.incidentReplyType === '01' && formData.newPrefix)
    // formData.incidentReplyType === '01' is for handling defaultValue
    optionalParams.push({
      type: AnnexedParameters.NUEVO_PREFIJO,
      value: formData.newPrefix
    })
  if (formData.newNumber)
    optionalParams.push({
      type: AnnexedParameters.NUEVO_NUMERO_TELEFONO,
      value: formData.newNumber
    })
  if (formData.newEmail)
    optionalParams.push({
      type: AnnexedParameters.NUEVO_EMAIL,
      value: formData.newEmail
    })

  if (procedureType === 'procedureDocument') {
    return optionalParams.map(param => ({
      ...param,
      idProcedure: itemId
    })) as ProcedureParameter[]
  }

  return optionalParams
}

export const mapClientNameOptions = (procedures: ProcedureModel[]): SelectOption[] => {
  const clientDataUnique = new Map<number, string>()

  procedures.forEach(procedure => {
    clientDataUnique.set(procedure.clientData?.id ?? 0, procedure.client ?? '')
  })

  const uniqueClientData = Array.from(clientDataUnique, ([id, name]) => ({ id, name }))

  return uniqueClientData.map(client => ({
    label: client.name,
    value: client.id
  }))
}
